import type { Theme } from '@material-ui/core'
import { Card, makeStyles, styled } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import {
  ActionButton,
  CustomBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  Hidden,
  TableRow,
  Typography,
  useClientOptions,
  useUser
} from '@paintscout/ui'
import { getObjectLabels } from '@paintscout/util/builder'
import type { OverridableValue } from 'paintscout'
import React from 'react'
import AreaSubstrateHeaderCell from './AreaSubstrateHeaderCell'
import AreaSubstrateItemCell from './AreaSubstrateItemCell'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(2),
    cursor: 'pointer',
    borderRadius: `${theme.borderRadius.md} ${theme.borderRadius.md} 0 0`,
    boxShadow: 'none',
    marginLeft: theme.spacing(-2)
  },
  tableHead: {
    height: theme.typography.pxToRem(36),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.borderRadius.md
  },
  actionColumn: {
    paddingLeft: `${theme.spacing(0.5)}px !important`,
    paddingRight: `${theme.spacing(0.5)}px !important`,
    height: `56px`
  },
  tableHeadCell: {
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
    fontWeight: theme.typography.fontWeightMedium
  },
  totalLabel: {
    ...theme.typography.overline
  }
}))

export interface TotalsColumnsProps {
  showMaterials?: boolean
  prep: OverridableValue
  hours: OverridableValue
  price: OverridableValue
  materials: OverridableValue

  onClick: (ev: React.MouseEvent) => any
}

const StyledTableCell = styled((props) => <TableCell {...props} />)({
  padding: '0.5em'
})

export default function TotalsColumns(props: TotalsColumnsProps) {
  const classes = useStyles(props)
  const { isViewer, isCrew } = useUser()
  const { showMaterials, onClick } = props
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })

  const rawHours = props.hours.useCustom ? props.hours.custom : props.hours.default
  const hours = typeof rawHours === 'string' ? parseFloat(rawHours) : rawHours
  const rawPrep = props.prep.useCustom ? props.prep.custom : props.prep.default
  const prep = typeof rawPrep === 'string' ? parseFloat(rawPrep) : rawPrep
  const rawPrice = (props.price.useCustom ? props.price.custom : props.price.default) || 0
  const price = typeof rawPrice === 'string' ? parseFloat(rawPrice) : rawPrice
  const rawMaterials = (props.materials.useCustom ? props.materials.custom : props.materials.default) || 0
  const materials = typeof rawMaterials === 'string' ? parseFloat(rawMaterials) : rawMaterials

  const totalHours = prep + hours
  const defaultHours = Number(props.prep.default) + Number(props.hours.default)
  const customTotalHours = totalHours !== defaultHours
  const totalPrice = price + materials
  const defaultPrice = Number(props.price.default) + Number(props.materials.default)
  const customTotalPrice = totalPrice !== defaultPrice

  return (
    <>
      {/* desktop */}
      <Hidden smDown>
        <AreaSubstrateItemCell>
          <div className={classes.totalLabel}>TOTAL</div>
        </AreaSubstrateItemCell>
        <AreaSubstrateItemCell width="small" />
        <AreaSubstrateItemCell align="right" isPrep width="small">
          <CustomBadge showBadge={props.prep.useCustom}>
            <Typography format={'hours'} component={'span'} value={prep} variant={'body1'} />
          </CustomBadge>
        </AreaSubstrateItemCell>
        {showMaterials && (
          <AreaSubstrateItemCell align="right" isPrep width="large">
            <CustomBadge showBadge={props.hours.useCustom}>
              <Typography format={'hours'} component={'span'} value={hours} variant={'body1'} />
            </CustomBadge>
          </AreaSubstrateItemCell>
        )}
        <AreaSubstrateItemCell align="right" isPrep width="small">
          <CustomBadge showBadge={customTotalHours}>
            <Typography format={'hours'} component={'span'} value={totalHours} variant={'body1'} />
          </CustomBadge>
        </AreaSubstrateItemCell>
        {showMaterials && (
          <AreaSubstrateItemCell align="right" width="large">
            <CustomBadge showBadge={materials && !!props.materials.useCustom}>
              <Typography format={'price'} component={'span'} value={materials} variant={'body1'} />
            </CustomBadge>
          </AreaSubstrateItemCell>
        )}
        <AreaSubstrateItemCell align="right" width="large">
          <CustomBadge showBadge={props.price.useCustom}>
            <Typography format={'price'} component={'span'} value={price} variant={'body1'} />
          </CustomBadge>
        </AreaSubstrateItemCell>
        {showMaterials && (
          <AreaSubstrateItemCell align="right" width="large">
            <CustomBadge showBadge={customTotalPrice}>
              <Typography format={'price'} component={'span'} value={totalPrice} variant={'body1'} />
            </CustomBadge>
          </AreaSubstrateItemCell>
        )}
        <AreaSubstrateItemCell isControl width="small" className={classes.actionColumn}>
          {onClick && (
            <ActionButton
              disabled={isViewer || isCrew}
              actions={onClick ? [{ key: 'adjust-totals', label: 'Adjust Totals', icon: EditIcon }] : []}
              onActionClick={(ev, action) => {
                if (action === 'adjust-totals') {
                  onClick(ev)
                }
              }}
            />
          )}
        </AreaSubstrateItemCell>
      </Hidden>
      {/* mobile */}
      <Hidden mdUp>
        <AreaSubstrateItemCell padding="none" colSpan={2}>
          <Card className={classes.root}>
            <Table component="table">
              <TableHead component="thead" className={classes.tableHead}>
                <TableRow component="tr">
                  <AreaSubstrateHeaderCell component="th" colSpan={2} className={classes.tableHeadCell}>
                    Totals
                  </AreaSubstrateHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody component="tbody">
                <TableRow component="tr">
                  <StyledTableCell component="td">{objectLabels.prep.value}</StyledTableCell>
                  <StyledTableCell component="td" align="right">
                    <Typography format={'hours'} component={'span'} value={prep} variant={'body1'} showUnits />
                  </StyledTableCell>
                </TableRow>
                {showMaterials && (
                  <TableRow component="tr">
                    <StyledTableCell component="td">{objectLabels.labour.value}</StyledTableCell>
                    <StyledTableCell component="td" align="right">
                      <Typography format={'hours'} component={'span'} value={hours} variant={'body1'} showUnits />
                    </StyledTableCell>
                  </TableRow>
                )}
                <TableRow component="tr">
                  <StyledTableCell component="td">Hours</StyledTableCell>
                  <StyledTableCell component="td" align="right">
                    <Typography format={'hours'} component={'span'} value={totalHours} variant={'body1'} showUnits />
                  </StyledTableCell>
                </TableRow>
                {showMaterials && (
                  <TableRow component="tr">
                    <StyledTableCell component="td">Materials</StyledTableCell>
                    <StyledTableCell component="td" align="right">
                      <Typography format={'price'} component={'span'} value={materials} variant={'body1'} showUnits />
                    </StyledTableCell>
                  </TableRow>
                )}
                <TableRow component="tr">
                  <StyledTableCell component="td">Labor</StyledTableCell>
                  <StyledTableCell component="td" align="right">
                    <Typography format={'price'} component={'span'} value={price} variant={'body1'} showUnits />
                  </StyledTableCell>
                </TableRow>
                {showMaterials && (
                  <TableRow component="tr">
                    <StyledTableCell component="td">Price</StyledTableCell>
                    <StyledTableCell component="td" align="right">
                      <Typography format={'price'} component={'span'} value={totalPrice} variant={'body1'} showUnits />
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </AreaSubstrateItemCell>
      </Hidden>
    </>
  )
}
